import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';
import axios from 'axios';
import moment from 'moment-timezone';
import SharedNav from './SharedNav';
import './Inbound.css';

// Scheduler stores the meeting date/time as two separate human-readable strings (e.g.
// "Thursday, September 10, 2026" + "11:00 AM") plus the IANA timezone the lead picked on the
// booking page -- not a single ISO timestamp. Parsed against explicit formats (not relying on
// locale-sensitive native Date parsing) so this can't silently misparse.
function parseMeeting(row) {
  if (!row.scheduled_date || !row.scheduled_time) return null;
  const combined = `${row.scheduled_date} ${row.scheduled_time}`;
  const m = moment.tz(combined, 'dddd, MMMM D, YYYY h:mm A', row.timezone || 'America/New_York');
  return m.isValid() ? m : null;
}

const STATUS_OPTIONS = ['Live', 'Follow up', 'Not Responding', 'Not Interested'];
const STATUS_CLASS = {
  'Live': 'inbound-status-live',
  'Follow up': 'inbound-status-followup',
  'Not Responding': 'inbound-status-notresponding',
  'Not Interested': 'inbound-status-notinterested',
};

const EDIT_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'op_type', label: 'Operation Type' },
  { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
  { key: 'scheduled_date', label: 'Meeting Date (e.g. Thursday, September 10, 2026)' },
  { key: 'scheduled_time', label: 'Meeting Time (e.g. 11:00 AM)' },
  { key: 'timezone', label: 'Timezone (e.g. America/New_York)' },
  { key: 'station_codes', label: 'Station Code(s), comma-separated' },
  { key: 'notes', label: 'Notes' },
];

function EditLeadModal({ row, onClose, onSaved, headers }) {
  const [form, setForm] = useState(() => {
    const init = {};
    EDIT_FIELDS.forEach(f => { init[f.key] = row[f.key] || ''; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.patch(`${API_URL}/api/scheduler-bookings/${row.id}`, form, { headers });
      onSaved({ ...row, ...form });
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Error saving lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inbound-modal-overlay" onClick={onClose}>
      <div className="inbound-modal" onClick={e => e.stopPropagation()}>
        <h3>Edit Lead</h3>
        <div className="inbound-modal-grid">
          {EDIT_FIELDS.map(f => (
            <div key={f.key} className="inbound-modal-field">
              <label>{f.label}</label>
              {f.key === 'notes' ? (
                <textarea rows={3} value={form[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
              ) : f.type === 'select' ? (
                <select value={form[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}>
                  <option value="">—</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type="text" value={form[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
              )}
            </div>
          ))}
        </div>
        {error && <p className="inbound-modal-error">{error}</p>}
        <div className="inbound-modal-actions">
          <button type="button" className="inbound-modal-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="inbound-modal-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Inbound({ user, onLogout }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({}); // `${id}:${field}` -> draft value
  const [editingRow, setEditingRow] = useState(null);
  const [creatingId, setCreatingId] = useState(null);
  const [createError, setCreateError] = useState({}); // id -> message
  // null = default (newest lead first, as loaded); 'asc'/'desc' = sorted by meeting time instead.
  const [meetingSort, setMeetingSort] = useState(null); // null = default (newest lead first) until clicked once

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/scheduler-bookings`, { headers });
      setRows(res.data || []);
    } catch (e) {
      console.error('Error loading inbound leads:', e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveField = async (id, field, value) => {
    const key = `${id}:${field}`;
    setSavingKey(key);
    try {
      await axios.patch(`${API_URL}/api/scheduler-bookings/${id}`, { [field]: value }, { headers });
      setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    } catch (e) {
      console.error(`Error saving ${field}:`, e);
    } finally {
      setSavingKey(null);
    }
  };

  // Rows without a parseable meeting time (e.g. Jolene's manual entry with no real timezone)
  // sort to the bottom regardless of direction, rather than clumping at whichever end
  // "no date" happens to land on for a given sort direction.
  const displayRows = useMemo(() => {
    if (!meetingSort) return rows;
    const withMeeting = rows.map(r => ({ row: r, m: parseMeeting(r) }));
    withMeeting.sort((a, b) => {
      if (!a.m && !b.m) return 0;
      if (!a.m) return 1;
      if (!b.m) return -1;
      return meetingSort === 'asc' ? a.m - b.m : b.m - a.m;
    });
    return withMeeting.map(x => x.row);
  }, [rows, meetingSort]);

  // Straight 2-state toggle: chronological <-> reverse chronological. No third "reset to
  // newest-lead-first" state -- that was confusing (looked like sorting had randomly stopped
  // working on the 3rd click).
  const toggleMeetingSort = () => {
    setMeetingSort(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleCreateAccount = async (row) => {
    if (!window.confirm(`Create a real company + customer login for "${row.company_name || row.name}"? This creates live records and emails hiring@.`)) return;
    setCreatingId(row.id);
    setCreateError(prev => ({ ...prev, [row.id]: null }));
    try {
      const res = await axios.post(`${API_URL}/api/scheduler-bookings/${row.id}/create-account`, {}, { headers });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, company_id: res.data.company_id } : r));
    } catch (e) {
      setCreateError(prev => ({ ...prev, [row.id]: e.response?.data?.error || 'Error creating account' }));
    } finally {
      setCreatingId(null);
    }
  };

  return (
    <div className="inbound-page">
      <SharedNav user={user} onLogout={onLogout} />
      <div className="inbound-content">
        <h1 className="inbound-title">Inbound</h1>
        <p className="inbound-subtitle">Every strategy call booking from the scheduler, newest lead first.</p>

        {loading ? (
          <p className="inbound-empty">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="inbound-empty">No inbound leads yet.</p>
        ) : (
          <div className="inbound-table-wrap">
            <table className="inbound-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Company Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th className="inbound-sortable-th" onClick={toggleMeetingSort}>
                    Meeting (IST){meetingSort === 'asc' ? ' ▲' : meetingSort === 'desc' ? ' ▼' : ''}
                  </th>
                  <th>Meeting (Local)</th>
                  <th>Station(s)</th>
                  <th>Proposal Auto-Sent</th>
                  <th>Proposal Link</th>
                  <th>Notes</th>
                  <th>Account</th>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map(row => {
                  const meeting = parseMeeting(row);
                  const noteDraft = drafts[`${row.id}:notes`] !== undefined ? drafts[`${row.id}:notes`] : (row.notes || '');
                  const companyDraft = drafts[`${row.id}:company_name`] !== undefined ? drafts[`${row.id}:company_name`] : (row.company_name || '');
                  return (
                    <tr key={row.id}>
                      <td className="inbound-name-cell">{row.name || '—'}</td>
                      <td className="inbound-center">
                        <select
                          className={`inbound-status-select ${STATUS_CLASS[row.status] || ''}`}
                          value={row.status || ''}
                          onChange={e => saveField(row.id, 'status', e.target.value)}
                        >
                          <option value="">—</option>
                          {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                        {savingKey === `${row.id}:status` && <span className="inbound-saving">Saving…</span>}
                      </td>
                      <td className="inbound-editable-cell">
                        <input
                          className="inbound-inline-input"
                          value={companyDraft}
                          placeholder="—"
                          onChange={e => setDrafts(prev => ({ ...prev, [`${row.id}:company_name`]: e.target.value }))}
                          onBlur={e => {
                            const val = e.target.value;
                            if (val !== (row.company_name || '')) saveField(row.id, 'company_name', val);
                          }}
                        />
                        {savingKey === `${row.id}:company_name` && <span className="inbound-saving">Saving…</span>}
                      </td>
                      <td>{row.email ? <a href={`mailto:${row.email}`} className="inbound-email-link">{row.email}</a> : '—'}</td>
                      <td>{row.phone || '—'}</td>
                      <td className="inbound-ist-cell">{meeting ? meeting.clone().tz('Asia/Kolkata').format('ddd, MMM D, h:mm A') : '—'}</td>
                      <td>{meeting ? meeting.format('ddd, MMM D, h:mm A') : '—'}</td>
                      <td title={row.stations_display || ''}>
                        {row.stations_display
                          ? (row.stations_display.length > 22 ? row.stations_display.slice(0, 22) + '…' : row.stations_display)
                          : '—'}
                      </td>
                      <td className="inbound-center">
                        {row.op_type === 'Linehaul' ? (
                          <span className="inbound-pill inbound-pill-na">N/A</span>
                        ) : row.proposal_sent ? (
                          <span className="inbound-pill inbound-pill-yes">Yes</span>
                        ) : (
                          <span className="inbound-pill inbound-pill-no">No</span>
                        )}
                      </td>
                      <td className="inbound-center">
                        {row.proposal_url ? (
                          <a href={row.proposal_url} target="_blank" rel="noopener noreferrer" className="inbound-proposal-link">View</a>
                        ) : '—'}
                      </td>
                      <td className="inbound-editable-cell">
                        <textarea
                          className="inbound-notes-input"
                          rows={1}
                          value={noteDraft}
                          placeholder="Add note…"
                          onChange={e => setDrafts(prev => ({ ...prev, [`${row.id}:notes`]: e.target.value }))}
                          onBlur={e => {
                            const val = e.target.value;
                            if (val !== (row.notes || '')) saveField(row.id, 'notes', val);
                          }}
                        />
                        {savingKey === `${row.id}:notes` && <span className="inbound-saving">Saving…</span>}
                      </td>
                      <td className="inbound-center">
                        {row.company_id ? (
                          <button type="button" className="inbound-view-account-btn" onClick={() => navigate('/users')}>
                            ✓ View Account
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="inbound-create-account-btn"
                              onClick={() => handleCreateAccount(row)}
                              disabled={creatingId === row.id}
                            >
                              {creatingId === row.id ? 'Creating…' : 'Create Account'}
                            </button>
                            {createError[row.id] && <span className="inbound-create-error">{createError[row.id]}</span>}
                          </>
                        )}
                      </td>
                      <td className="inbound-center">
                        <button type="button" className="inbound-edit-btn" onClick={() => setEditingRow(row)}>Edit</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingRow && (
        <EditLeadModal
          row={editingRow}
          headers={headers}
          onClose={() => setEditingRow(null)}
          onSaved={updated => setRows(prev => prev.map(r => r.id === updated.id ? updated : r))}
        />
      )}
    </div>
  );
}
